import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useInventory } from '../../contexts/InventoryContext';
import { describeRestore, restoreProductsFromBackup } from '../../lib/product-restore';
import {
  Download,
  Upload,
  HardDrive,
  Calendar,
  Clock,
  Archive,
  CheckCircle,
  AlertTriangle,
  FileCheck
} from 'lucide-react';
import { migrateLegacyStorageKey, resolveTenantAdminId } from '../../lib/tenantStorage';

interface BackupData {
  timestamp: string;
  type: 'manual' | 'daily' | 'monthly';
  size: number;
  compressed: boolean;
  items_count: number;
  shipments_count: number;
  users_count?: number;
}

interface BackupFile extends BackupData {
  id: string;
  filename: string;
  data: any;
}

function backupsStorageKey(adminId: string): string {
  return `system_backups:${adminId}`;
}

function lastAutoBackupKey(adminId: string): string {
  return `last_auto_backup:${adminId}`;
}

/** Keeps the newest daily backups; manual and monthly archives are never pruned. */
function pruneDailyBackups(all: BackupFile[]): BackupFile[] {
  let dailySeen = 0;

  return all.filter(backup => {
    if (backup.type !== 'daily') return true;
    dailySeen += 1;
    return dailySeen <= MAX_DAILY_BACKUPS;
  });
}

function migrateLegacyBackupKeys(adminId: string): void {
  migrateLegacyStorageKey('system_backups', backupsStorageKey(adminId));
  migrateLegacyStorageKey('last_auto_backup', lastAutoBackupKey(adminId));
}

export const BackupManager: React.FC = () => {
  const { user } = useAuth();
  const { shipments, fetchAllItems } = useInventory();
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastAutoBackup, setLastAutoBackup] = useState<Date | null>(null);

  const adminId = resolveTenantAdminId(user, null);

  useEffect(() => {
    if (!adminId) return;
    migrateLegacyBackupKeys(adminId);
    loadBackups(adminId);
    checkAutoBackupSchedule(adminId);
  }, [adminId]);

  const loadBackups = (tenantAdminId: string) => {
    try {
      const savedBackups = localStorage.getItem(backupsStorageKey(tenantAdminId));
      if (savedBackups) {
        setBackups(JSON.parse(savedBackups));
      }

      const lastAuto = localStorage.getItem(lastAutoBackupKey(tenantAdminId));
      if (lastAuto) {
        setLastAutoBackup(new Date(lastAuto));
      }
    } catch (error) {
      console.error('Failed to load backups:', error);
    }
  };

  /**
   * Saves the backup list, giving up the oldest entries if the browser refuses
   * the write. A full-catalogue snapshot is far larger than the page-sized
   * snapshots this list used to hold, so the storage quota is reachable.
   */
  const persistBackups = (
    all: BackupFile[],
  ): { saved: BackupFile[]; dropped: number } => {
    if (!adminId) return { saved: all, dropped: 0 };

    let candidates = pruneDailyBackups(all);
    let dropped = 0;

    while (candidates.length > 0) {
      try {
        localStorage.setItem(backupsStorageKey(adminId), JSON.stringify(candidates));
        return { saved: candidates, dropped };
      } catch {
        // Out of room. Drop the oldest and retry; the newest is the one the user
        // just asked for, so it is the last to go.
        candidates = candidates.slice(0, -1);
        dropped += 1;
      }
    }

    return { saved: [], dropped };
  };

  const createBackup = async (type: 'manual' | 'daily' | 'monthly') => {
    setLoading(true);

    try {
      // A backup has to cover the whole catalogue. The inventory context's
      // `items` holds only the page currently on screen, which would quietly
      // produce a backup of a couple of dozen products.
      const allItems = await fetchAllItems(true);

      if (allItems.length === 0) {
        alert('There are no products to back up yet.');
        return;
      }

      const backupData = {
        metadata: {
          timestamp: new Date().toISOString(),
          type,
          app_version: '1.0.0',
          user_id: user?.id,
          created_by: user?.username,
        },
        inventory: allItems,
        shipments,
        settings: {
          theme: localStorage.getItem('theme'),
          language: localStorage.getItem('language'),
        },
      };

      const backup: BackupFile = {
        id: `backup_${Date.now()}`,
        filename: `inventory_backup_${type}_${new Date().toISOString().split('T')[0]}.json`,
        timestamp: new Date().toISOString(),
        type,
        size: JSON.stringify(backupData).length,
        compressed: false,
        items_count: allItems.length,
        shipments_count: shipments.length,
        data: backupData,
      };

      const { saved, dropped } = persistBackups([backup, ...backups]);
      setBackups(saved);

      if (saved.length === 0) {
        alert(
          'This backup is too large for the browser to store. Use "Create Manual Backup" and '
          + 'download the file straight away to keep a copy.',
        );
        return;
      }

      if (type === 'daily' && adminId) {
        const now = new Date();
        setLastAutoBackup(now);
        localStorage.setItem(lastAutoBackupKey(adminId), now.toISOString());
      }

      alert(
        `${type.charAt(0).toUpperCase() + type.slice(1)} backup created with ${allItems.length} products.`
        + (dropped > 0
          ? `\n\n${dropped} older backup${dropped === 1 ? '' : 's'} had to be removed to make room in browser storage.`
          : ''),
      );
    } catch (error) {
      console.error('Backup creation error:', error);
      alert(
        `Failed to create backup: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadBackup = (backup: BackupFile) => {
    const blob = new Blob([JSON.stringify(backup.data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = backup.filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Restores products from a backup by reconciling rather than replacing.
   *
   * Products are matched on SKU server-side: existing ones are updated in place
   * and missing ones are added, all in one transaction. Nothing is deleted, so
   * sales and invoice history stay attached, and stock is left alone because it
   * belongs to per-location inventory rather than to the product row.
   */
  const restoreBackup = async (backup: BackupFile) => {
    const products = backup.data?.inventory;

    if (!Array.isArray(products) || products.length === 0) {
      alert('This backup does not contain any products to restore.');
      return;
    }

    if (user?.role === 'super_admin') {
      alert('Super admin must specify which account to restore to');
      return;
    }

    const confirmed = confirm(
      `Restore ${products.length} product${products.length === 1 ? '' : 's'} from this backup?\n\n`
      + 'Products are matched by SKU: existing ones are updated and any that are missing get '
      + 'added back. Nothing is deleted, and stock levels are left unchanged.',
    );

    if (!confirmed) return;

    setLoading(true);

    const result = await restoreProductsFromBackup({ items: products });

    setLoading(false);

    if (!result.success || !result.summary) {
      alert(`Failed to restore backup: ${result.error ?? 'Unknown error'}`);
      return;
    }

    if (backup.data?.settings?.theme) {
      localStorage.setItem('theme', backup.data.settings.theme);
    }
    if (backup.data?.settings?.language) {
      localStorage.setItem('language', backup.data.settings.language);
    }

    alert(
      `Backup restored.\n\n${describeRestore(result.summary)}\n\n`
      + 'The page will reload to show the restored data.',
    );

    window.location.reload();
  };

  const checkAutoBackupSchedule = (tenantAdminId: string) => {
    const now = new Date();
    const lastBackup = localStorage.getItem(lastAutoBackupKey(tenantAdminId));
    
    if (lastBackup) {
      const lastBackupDate = new Date(lastBackup);
      const hoursSinceLastBackup = (now.getTime() - lastBackupDate.getTime()) / (1000 * 60 * 60);
      
      // Auto backup daily at 19:00 (7 PM)
      if (now.getHours() === 19 && hoursSinceLastBackup >= 23) {
        createBackup('daily');
      }
      
      // Monthly backup on 1st day
      if (now.getDate() === 1 && hoursSinceLastBackup >= 24 * 28) {
        createBackup('monthly');
      }
    }
  };

  const uploadBackup = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.json.gz';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setLoading(true);

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const content = event.target?.result as string;
            const backupData = JSON.parse(content);

            // Validate backup structure
            if (!backupData.metadata || !backupData.inventory) {
              throw new Error('Invalid backup file format');
            }

            // Create a BackupFile object from uploaded data
            const uploadedBackup: BackupFile = {
              id: `uploaded_${Date.now()}`,
              filename: file.name,
              timestamp: backupData.metadata.timestamp || new Date().toISOString(),
              type: 'manual',
              size: content.length,
              compressed: file.name.endsWith('.gz'),
              items_count: backupData.inventory?.length || 0,
              shipments_count: backupData.shipments?.length || 0,
              data: backupData
            };

            // Restore the uploaded backup
            await restoreBackup(uploadedBackup);
          } catch (parseError) {
            setLoading(false);
            alert('Failed to parse backup file. Please ensure it is a valid backup file.');
            console.error('Parse error:', parseError);
          }
        };
        reader.readAsText(file);
      } catch (error) {
        setLoading(false);
        alert('Failed to read backup file');
        console.error('Upload error:', error);
      }
    };
    input.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const dailyBackups = backups.filter(b => b.type === 'daily');
  const monthlyBackups = backups.filter(b => b.type === 'monthly');
  const manualBackups = backups.filter(b => b.type === 'manual');

  return (
    <div className="space-y-6">
      {/* Backup Controls */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <HardDrive className="h-5 w-5 mr-2" />
          Backup & Recovery System
        </h3>
        
        {/* Auto Backup Status */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4 border border-blue-200 dark:border-blue-800">
          <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
            🤖 Automatic Backup Schedule
          </h4>
          <div className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
            <div>📅 Daily Backups: Every day at 19:00 (7 PM)</div>
            <div>🗓️ Monthly Archives: 1st day of each month</div>
            <div>📦 Each backup covers your full product catalogue</div>
            {lastAutoBackup && (
              <div>⏰ Last Auto Backup: {lastAutoBackup.toLocaleString()}</div>
            )}
          </div>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-3">
            Backups are held in this browser only and are cleared when you log out. Download any
            backup you want to keep. Restoring matches products by SKU — it updates and adds, never
            deletes, and never changes stock levels.
          </p>
        </div>

        {/* Manual Backup */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => createBackup('manual')}
            variant="primary"
            loading={loading}
          >
            <Download className="h-4 w-4 mr-2" />
            Create Manual Backup
          </Button>

          <Button
            onClick={() => createBackup('monthly')}
            variant="secondary"
            loading={loading}
          >
            <Archive className="h-4 w-4 mr-2" />
            Create Monthly Archive
          </Button>

          <Button
            onClick={uploadBackup}
            variant="secondary"
            loading={loading}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload & Restore Backup
          </Button>
        </div>
      </div>

      {/* Backup Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-bold text-blue-600">{backups.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Backups</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-600">{dailyBackups.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Daily Backups</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-blue-500">{monthlyBackups.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Monthly Archives</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-orange-600">
            {formatFileSize(backups.reduce((sum, b) => sum + b.size, 0))}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Size</div>
        </div>
      </div>

      {/* Backup History */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Backup History
        </h3>
        
        {backups.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Archive className="h-12 w-12 mx-auto mb-4" />
            <p>No backups created yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {backups.map((backup) => (
                  <tr key={backup.id}>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        backup.type === 'monthly' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                        backup.type === 'daily' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {backup.type === 'monthly' ? '🗓️ Monthly' :
                         backup.type === 'daily' ? '📅 Daily' : '🔧 Manual'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {new Date(backup.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatFileSize(backup.size)}
                      {backup.compressed && (
                        <span className="ml-1 text-green-600">📦</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {backup.items_count} items, {backup.shipments_count} shipments
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => downloadBackup(backup)}
                          variant="secondary"
                          size="sm"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => restoreBackup(backup)}
                          variant="secondary"
                          size="sm"
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const MAX_DAILY_BACKUPS = 10;